import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { lovableDemoMembership } from "@/lib/lovable-demo-access";
import { divisionsQueryOptions, identityQueryOptions } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { WarrantyProvider } from "@/lovable-demo/lib/warranty/store";
import { initializeDemoSession, supabase } from "@/lovable-demo/runtime/client";

export const Route = createFileRoute("/demo")({ component: LovableDemoRoute });

function LovableDemoRoute() {
  const { client, tenantContext } = usePortalRuntime();
  // Bootstrap has already populated these verified caches. The demo never
  // starts a new authentication, identity or operational-data request.
  const identity = useQuery({ ...identityQueryOptions(client), enabled: false });
  const memberships = useQuery({ ...divisionsQueryOptions(client), enabled: false });
  const membership = lovableDemoMembership(tenantContext, identity.data, memberships.data);
  if (!membership || !identity.data) {
    return (
      <main className="mx-auto max-w-3xl space-y-4 px-6 py-12">
        <h1 className="text-2xl font-semibold">Synthetic workflow demo unavailable</h1>
        <p role="status">
          A verified portal account, selected Division membership and Problem Record read access are
          required. Demo personas do not change your portal permissions.
        </p>
        <Link to="/" className="text-primary underline">
          Back to portal
        </Link>
      </main>
    );
  }
  return (
    <DemoSession
      key={`${tenantContext.tenantId}:${membership.membershipId}:${identity.data.userId}`}
      tenantId={tenantContext.tenantId}
      membershipId={`${membership.membershipId}:${identity.data.userId}`}
    />
  );
}

function DemoSession({ tenantId, membershipId }: { tenantId: string; membershipId: string }) {
  const [cache] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
      }),
  );
  const [ready, setReady] = useState(false);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let dispose: (() => void) | undefined;
    let unsubscribe: (() => void) | undefined;
    try {
      dispose = initializeDemoSession({ tenantId, membershipId });
      const subscription = supabase.auth.onAuthStateChange((event) => {
        if (event === "USER_UPDATED" || event === "INITIAL_SESSION") {
          cache.clear();
          setRevision((value) => value + 1);
        }
      });
      unsubscribe = () => subscription.data.subscription.unsubscribe();
      setReady(true);
    } catch {
      setError(
        "The synthetic workspace could not be opened. Operational data was not used as a substitute.",
      );
    }
    return () => {
      unsubscribe?.();
      cache.clear();
      dispose?.();
    };
  }, [cache, membershipId, tenantId]);

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      data-testid="lovable-synthetic-workspace"
    >
      <section
        aria-label="Synthetic demonstration notice"
        className="border-b border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm"
      >
        <strong>SYNTHETIC · DEMONSTRATION ONLY</strong>
        <p className="mt-1">
          Latest Lovable snapshot: 4 programs, 14 Problem Records and 121 sample claims. Observation
          date: November 3, 2026. Personas, approvals and outcomes are simulated. Demo changes stay
          in this browser; production data and schedules are unchanged.
        </p>
      </section>
      {error ? (
        <p role="alert" className="p-6">
          {error}
        </p>
      ) : !ready ? (
        <p role="status" className="p-6">
          Opening synthetic workspace…
        </p>
      ) : (
        <QueryClientProvider client={cache}>
          <WarrantyProvider key={revision}>
            <Outlet />
          </WarrantyProvider>
        </QueryClientProvider>
      )}
    </div>
  );
}
